import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { resetAbility } from '@/boot/plugins'
import type { AuthSession, AuthUser } from '@/types/auth'
import { getAuthCookie, removeAuthCookie, setAuthCookie } from '@/utils/auth-cookie'
import { clearIdleStorage, writeLastActivity } from '@/utils/idle-session'

/**
 * State only — no HTTP. Fetching the profile lives in `useSession`, so this
 * store stays the single place that answers "who is signed in?" without
 * depending on the transport.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null)
  // Seeded from the cookie so a reload is already signed in before the profile
  // has been fetched — the guard can answer on the very first navigation.
  const token = ref<string | null>(getAuthCookie())

  const isLoggedIn = computed(() => token.value !== null)

  function setSession(session: AuthSession): void {
    setAuthCookie(session.token)
    token.value = session.token
    user.value = session.user
    // A new sign-in must not be judged by a stamp left over from an earlier session.
    writeLastActivity(Date.now())
  }

  function setUser(next: AuthUser): void {
    user.value = next
  }

  function clearSession(): void {
    removeAuthCookie()
    token.value = null
    user.value = null
    // Without this the next person to sign in on this machine inherits the
    // previous user's permissions for the frame before their profile loads.
    resetAbility()
    clearIdleStorage()
  }

  return { user, token, isLoggedIn, setSession, setUser, clearSession }
})
